































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';

import type { User } from '@/models/user';
import type { Trade } from '@/models/trade';
import type { Phase } from '@/models/phase';
import type { CompanySetting } from '@/models/companySetting';
import type { Concept } from '@/models/concept';
import type { Tag } from '@/models/tag';

import { phaseWebservice } from '@/webservices/phase';
import { tradeWebservice } from '@/webservices/trade';
import { conceptWebservice } from '@/webservices/concept';
import { tagWebservice } from '@/webservices/tag';

import SearchField from '@/components/prine/SearchField.vue';
import FilteringSetting from '@/components/knowledge/concepts/FilteringSetting.vue';

import { FilterManager } from '@/helpers/filter/FilterManager';
import { FilterOptions } from '@/helpers/filter/models/FilterOptions';
import MoreInfoModal from '@/components/MoreInfoModal.vue';
import LessonsList from '@/components/knowledge/lessons/LessonsList.vue';
import type { Lesson } from '@/models/lesson';

@Component({
    name: 'DetailedConceptView',
    components: {
        SearchField,
        FilteringSetting,
        MoreInfoModal,
        LessonsList,
    },
})
export default class DetailedConceptView extends Vue {
    private filteredElements = new Array<Trade>();
    private tradesByPhase = new Array<Trade>();
    private originalTrades = new Array<Trade>();
    private phases = new Array<Phase>();
    private tags = new Array<Tag>();

    private selectedPhase: Phase = {} as Phase;
    private allPhases: Phase = {} as Phase;
    private selectedTag = {} as Tag;
    private allTags: Tag = {} as Tag;
    private selectedConcept: Concept = {} as Concept;

    private isSearching: boolean = false;
    private searchQuery: string = '';

    private isFilteringSettingActive = false;

    private filterManager: FilterManager = new FilterManager();
    private filterOptions: FilterOptions = {} as FilterOptions;

    private showOnlyMyLessons: boolean = false;

    private tableCollapsed: boolean = false;
    private openedDetailRows: number[] = [];

    private moreInfoShowModalActive: boolean = false;
    private moreInfoModalInsight: string;
    private moreInfoModalContent: string;
    private moreInfoModalId: number;

    @Getter('account/userInfo') private userInfo: User;
    @Getter('account/companySetting') private companySetting: CompanySetting;

    @Watch('searchQuery')
    private onSearchQueryValueChange() {
        this.search();
    }

    @Watch('showEmptyTrades')
    private onFiltersChanged() {
        this.filter();
    }

    private async mounted() {
        if (this.$route.query.hasOwnProperty('id')) {
            await this.loadConcept();
            await this.loadPhases();
            this.loadTags();
            this.loadTrades();
        } else {
            await this.$router.push({ path: '/knowledge/combined' });
        }
    }

    private init() {
        this.openedDetailRows = this.filteredElements.map((el) => el.id);
    }

    private initializeMoreInfoModal(lesson: Lesson) {
        this.moreInfoModalContent = lesson.more_info;
        this.moreInfoModalInsight = lesson.insight;
        this.moreInfoModalId = lesson.id;
        this.moreInfoShowModalActive = true;
    }

    private async loadConcept() {
        // Fetch the corresponding concept
        try {
            this.selectedConcept = await conceptWebservice.getById(Number(this.$route.query.id));
        } catch (error) {
            // Not found or server error
        }
    }

    private goBack() {
        this.$router.push({
            name: 'GlobalTopics',
            query: {
                searchQuery: encodeURIComponent(this.searchQuery),
                filterByLessons: this.$route.query.filterByLessons,
            },
        });
    }

    private loadPhases() {
        phaseWebservice.load().then((response) => {
            this.phases = response.data;

            this.allPhases.id = 0;
            this.allPhases.abbreviation = this.$t('keywords.all').toString();
            this.allPhases.title = this.$t('keywords.allPhases').toString();

            // getting selectedPhaseId from params
            const selectedPhaseId = Number.parseInt(this.$route.query.pid.toString(), 10);
            // finding the phase with selectedPhaseId
            const phase = this.phases.find((el) => el.id === selectedPhaseId);

            if (phase) {
                this.selectedPhase = phase;
            } else {
                this.selectedPhase = this.allPhases;
            }

            // Add it in the front of the array
            this.phases.unshift(this.allPhases);
        });
    }

    private loadTags() {
        tagWebservice
            .load()
            .then((response) => {
                this.tags = response.data;

                this.allTags.id = 0;
                this.allTags.title = this.$t('keywords.allTags').toString();

                this.selectedTag = this.allTags;

                // Add it in the front of the array
                this.tags.unshift(this.allTags);
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private loadTrades() {
        tradeWebservice
            .ByTopicIdeWithLessons(this.selectedConcept.topic_id)
            .then((response) => {
                this.tradesByPhase = response.data;
                this.filteredElements = response.data;
                this.originalTrades = response.data;

                if (this.$route.query.sq && this.$route.query.sq !== '') {
                    this.searchQuery = decodeURIComponent(this.$route.query.sq.toString());
                }
                this.filter();
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private updateSearchQuery(value: string) {
        this.searchQuery = value;
    }

    private search() {
        this.isSearching = true;
        this.filter();
        this.isSearching = false;
    }

    private clearSearch() {
        this.searchQuery = '';
    }

    get showEmptyTrades() {
        return this.$store.getters['knowledge/showEmptyTrades'];
    }

    set showEmptyTrades(value: boolean) {
        this.$store.commit('knowledge/setShowEmptyTrades', value);
    }

    private toggleFilteringSettings() {
        this.isFilteringSettingActive = !this.isFilteringSettingActive;
    }

    private showOnlyMyLessonsChanged(value: boolean) {
        this.showOnlyMyLessons = value;
        this.filter();
    }

    private phaseChanged(value: Phase) {
        this.selectedPhase = value;
        this.filter();
    }

    private tagChanged(value: Tag) {
        this.selectedTag = value;
        this.filter();
    }

    private setFilterOptions() {
        this.filterOptions.userId = this.userInfo.id;
        this.filterOptions.searchQuery = this.searchQuery;
        this.filterOptions.tag = this.selectedTag;
        this.filterOptions.phase = this.selectedPhase;
        this.filterOptions.showEmptyElements = this.showEmptyTrades;
        this.filterOptions.onlyMyLessons = this.showOnlyMyLessons;
    }

    private filter() {
        const data = JSON.parse(JSON.stringify(this.originalTrades));
        this.setFilterOptions();
        this.filteredElements = (this.filterManager.filter(data, this.filterOptions) as unknown) as Trade[];
        this.init();
    }

    get canResetFilter() {
        return (
            this.selectedTag !== this.allTags ||
            this.selectedPhase !== this.allPhases ||
            this.showEmptyTrades ||
            this.showOnlyMyLessons
        );
    }

    private resetFilter() {
        this.selectedTag = this.allTags;
        this.selectedPhase = this.allPhases;
        this.showEmptyTrades = false;
        this.showOnlyMyLessons = false;
        this.filter();
    }

    private toggleDetailRows() {
        if (this.tableCollapsed) {
            this.openedDetailRows = this.filteredElements.map((el) => el.id);
        } else {
            this.openedDetailRows = [];
        }
        this.tableCollapsed = !this.tableCollapsed;
    }

    private getLessonsCount(lessons: Lesson[]) {
        const count = (cLessons: Lesson[]): number => {
            return cLessons.reduce((base, lesson) => {
                if (lesson.sub_lessons) {
                    base += count(lesson.sub_lessons);
                }

                return base + 1;
            }, 0);
        };

        return count(lessons);
    }
}
